/**
 * ROOPBANI — Premium Cinematic Streetwear
 * Modern Vanilla JavaScript Architecture
 */

(function () {
  'use strict';

  /* --------------------------------------------------------------------------
     1. Sticky & Transparent Header Controller
     -------------------------------------------------------------------------- */
  const HeaderController = {
    init() {
      const header = document.querySelector('.rb-header');
      if (!header) return;

      const isSticky = header.getAttribute('data-sticky') === 'true';
      if (!isSticky) return;

      const handleScroll = () => {
        if (window.scrollY > 40) {
          header.classList.add('rb-header--scrolled');
        } else {
          header.classList.remove('rb-header--scrolled');
        }
      };

      window.addEventListener('scroll', handleScroll, { passive: true });
      handleScroll();
    }
  };

  /* --------------------------------------------------------------------------
     2. Hero Slider Controller (Touch Swipe + Auto-slide + Controls)
     -------------------------------------------------------------------------- */
  const HeroSliderController = {
    init() {
      const sliders = document.querySelectorAll('.rb-hero-slider-section');
      sliders.forEach((section) => this.setupSlider(section));
    },

    setupSlider(section) {
      const slider = section.querySelector('.rb-hero-slider');
      if (!slider) return;

      const slides = slider.querySelectorAll('.rb-hero-slide');
      if (slides.length <= 1) return;

      const dots = slider.querySelectorAll('.rb-hero-dot');
      const prevBtn = slider.querySelector('[data-slide-prev]');
      const nextBtn = slider.querySelector('[data-slide-next]');
      const autoplay = section.getAttribute('data-autoplay') === 'true';
      const intervalMs = parseInt(section.getAttribute('data-interval'), 10) || 6000;

      let currentIndex = 0;
      let timer = null;
      let touchStartX = 0;
      let touchEndX = 0;
      let touchStartY = 0;
      let touchEndY = 0;

      const goToSlide = (index) => {
        slides[currentIndex].classList.remove('is-active');
        if (dots[currentIndex]) {
          dots[currentIndex].classList.remove('is-active');
          dots[currentIndex].setAttribute('aria-selected', 'false');
        }

        currentIndex = (index + slides.length) % slides.length;

        slides[currentIndex].classList.add('is-active');
        if (dots[currentIndex]) {
          dots[currentIndex].classList.add('is-active');
          dots[currentIndex].setAttribute('aria-selected', 'true');
        }
      };

      const nextSlide = () => goToSlide(currentIndex + 1);
      const prevSlide = () => goToSlide(currentIndex - 1);

      const startTimer = () => {
        if (!autoplay) return;
        stopTimer();
        timer = setInterval(nextSlide, intervalMs);
      };

      const stopTimer = () => {
        if (timer) clearInterval(timer);
      };

      // Events
      if (nextBtn) {
        nextBtn.addEventListener('click', (e) => {
          e.preventDefault();
          nextSlide();
          startTimer();
        });
      }

      if (prevBtn) {
        prevBtn.addEventListener('click', (e) => {
          e.preventDefault();
          prevSlide();
          startTimer();
        });
      }

      dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const target = parseInt(dot.getAttribute('data-slide-target'), 10);
          goToSlide(target);
          startTimer();
        });
      });

      // Pause on hover
      slider.addEventListener('mouseenter', stopTimer);
      slider.addEventListener('mouseleave', startTimer);

      // Touch Swipe for Mobile (Unlocks vertical scrolling, strictly triggers horizontal slide changes)
      slider.addEventListener('touchstart', (e) => {
        if (!e.touches || e.touches.length > 1) return;
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        stopTimer();
      }, { passive: true });

      slider.addEventListener('touchend', (e) => {
        if (!e.changedTouches || e.changedTouches.length === 0) return;
        touchEndX = e.changedTouches[0].clientX;
        touchEndY = e.changedTouches[0].clientY;
        const diffX = touchStartX - touchEndX;
        const diffY = touchStartY - touchEndY;

        // Only navigate slides if horizontal swipe is clearly dominant over vertical scrolling
        if (Math.abs(diffX) > 40 && Math.abs(diffX) > Math.abs(diffY) * 1.3) {
          if (diffX > 0) nextSlide();
          else prevSlide();
        }
        startTimer();
      }, { passive: true });

      startTimer();
    }
  };

  /* --------------------------------------------------------------------------
     3. Cart Drawer & AJAX Cart Engine
     -------------------------------------------------------------------------- */
  const CartController = {
    init() {
      this.drawer = document.getElementById('RoopbaniCartDrawer');
      this.headerCountBadges = document.querySelectorAll('[data-header-cart-count]');
      this.bindEvents();
    },

    bindEvents() {
      // Open / Close triggers
      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-cart-open]')) {
          e.preventDefault();
          this.open();
        } else if (e.target.closest('[data-cart-close]')) {
          e.preventDefault();
          this.close();
        }
      });

      // ESC key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
        }
      });

      // Intercept Add to Cart forms (Product page & Quick Add)
      document.addEventListener('submit', (e) => {
        const form = e.target.closest('form[data-type="add-to-cart-form"]');
        if (!form) return;

        e.preventDefault();
        this.handleAddToCart(form);
      });

      // Quantity buttons inside Cart Drawer
      if (this.drawer) {
        this.drawer.addEventListener('click', (e) => {
          const qtyBtn = e.target.closest('[data-qty-change]');
          if (qtyBtn) {
            const delta = parseInt(qtyBtn.getAttribute('data-qty-change'), 10);
            const lineKey = qtyBtn.getAttribute('data-key');
            const input = this.drawer.querySelector(`input.rb-qty-input[data-key="${lineKey}"]`);
            if (input) {
              const currentVal = parseInt(input.value, 10) || 0;
              const newVal = Math.max(0, currentVal + delta);
              input.value = newVal;
              this.updateItemQuantity(lineKey, newVal);
            }
          }

          const removeBtn = e.target.closest('[data-remove-key]');
          if (removeBtn) {
            const lineKey = removeBtn.getAttribute('data-remove-key');
            this.updateItemQuantity(lineKey, 0);
          }
        });

        this.drawer.addEventListener('change', (e) => {
          const input = e.target.closest('input.rb-qty-input[data-key]');
          if (input) {
            const lineKey = input.getAttribute('data-key');
            const newVal = Math.max(0, parseInt(input.value, 10) || 0);
            this.updateItemQuantity(lineKey, newVal);
          }
        });
      }
    },

    isOpen() {
      return this.drawer && this.drawer.classList.contains('is-active');
    },

    open() {
      if (!this.drawer) return;
      this.drawer.classList.add('is-active');
      this.drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    },

    close() {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-active');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    },

    async handleAddToCart(form) {
      const submitBtn = form.querySelector('[type="submit"]');
      const originalText = submitBtn ? submitBtn.innerHTML : '';
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.innerHTML = '<span>ADDING TO BAG...</span>';
      }

      try {
        const formData = new FormData(form);
        const res = await fetch(window.Roopbani?.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          body: formData,
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });

        if (!res.ok) {
          throw new Error('Could not add item to bag');
        }

        await this.refreshDrawer();
        this.open();
      } catch (err) {
        console.error('Add to cart error:', err);
        alert('Could not add product to bag. Please try again.');
      } finally {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.innerHTML = originalText;
        }
      }
    },

    async updateItemQuantity(key, quantity) {
      try {
        const res = await fetch(window.Roopbani?.routes?.cart_change_url || '/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ id: key, quantity })
        });

        if (!res.ok) throw new Error('Error updating cart');

        await this.refreshDrawer();
      } catch (err) {
        console.error('Cart quantity change error:', err);
      }
    },

    async refreshDrawer() {
      try {
        const res = await fetch('/?section_id=cart-drawer');
        const html = await res.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const newDrawer = doc.getElementById('RoopbaniCartDrawer');
        if (newDrawer && this.drawer) {
          this.drawer.innerHTML = newDrawer.innerHTML;
        }

        // Update total count
        const countEl = this.drawer ? this.drawer.querySelector('[data-cart-count]') : null;
        if (countEl) {
          const match = countEl.textContent.match(/\d+/);
          const count = match ? match[0] : '0';
          this.headerCountBadges.forEach(b => (b.textContent = count));
        }
      } catch (err) {
        console.error('Error refreshing cart drawer:', err);
      }
    }
  };

  /* --------------------------------------------------------------------------
     4. Product Variant Selector Engine
     -------------------------------------------------------------------------- */
  const VariantController = {
    init() {
      const productForms = document.querySelectorAll('[data-variant-selectors], [data-product-variants]');
      productForms.forEach((wrap) => this.setupVariants(wrap));
    },

    setupVariants(container) {
      const productSection = container.closest('[data-section-id]') || document;
      const masterSelect = productSection.querySelector('[data-master-select]');
      if (!masterSelect) return;

      const radios = container.querySelectorAll('.rb-pill-input');
      const priceRegular = productSection.querySelector('[data-regular-price]');
      const priceCompare = productSection.querySelector('[data-compare-price]');
      const addToCartBtn = productSection.querySelector('[data-add-to-cart]');
      const addToCartText = productSection.querySelector('[data-add-to-cart-text]');

      const updateSelectedVariant = () => {
        // Collect current selections
        const selectedOptions = [];
        const checkedInputs = container.querySelectorAll('.rb-pill-input:checked');
        checkedInputs.forEach(input => selectedOptions.push(input.value.trim()));

        // Update header label texts (e.g. Size: M)
        checkedInputs.forEach(input => {
          const pos = input.getAttribute('data-option-position');
          const headerLabel = productSection.querySelector(`[data-option-header-value="${pos}"]`);
          if (headerLabel) headerLabel.textContent = input.value;
        });

        // Find matching variant in master select
        let matchedOption = null;
        const options = Array.from(masterSelect.options);

        // Simple single or multi-option title matching
        for (const opt of options) {
          const title = opt.textContent.split(' - ')[0].trim();
          const titleParts = title.split(' / ').map(t => t.trim());
          const match = selectedOptions.every(val => titleParts.includes(val));
          if (match) {
            matchedOption = opt;
            break;
          }
        }

        if (matchedOption) {
          masterSelect.value = matchedOption.value;
          const isAvailable = matchedOption.getAttribute('data-available') === 'true';
          const price = matchedOption.getAttribute('data-price');
          const comparePrice = matchedOption.getAttribute('data-compare-price');

          // Update Price
          if (priceRegular && price) {
            priceRegular.textContent = formatMoney(price);
          }
          if (priceCompare) {
            if (comparePrice && parseInt(comparePrice, 10) > parseInt(price, 10)) {
              priceCompare.textContent = formatMoney(comparePrice);
              priceCompare.classList.remove('rb-hide');
            } else {
              priceCompare.classList.add('rb-hide');
            }
          }

          // Update Discount Badge
          const discountBadge = productSection.querySelector('[data-discount-badge]');
          if (discountBadge) {
            if (comparePrice && parseInt(comparePrice, 10) > parseInt(price, 10)) {
              const p = parseInt(price, 10);
              const cp = parseInt(comparePrice, 10);
              const savings = Math.round(((cp - p) / cp) * 100);
              discountBadge.textContent = `-${savings}% OFF`;
              discountBadge.classList.remove('rb-hide');
            } else {
              discountBadge.classList.add('rb-hide');
            }
          }

          // Update CTA & Stock Badge
          const stockBadge = productSection.querySelector('[data-stock-badge]');
          if (addToCartBtn && addToCartText) {
            if (isAvailable) {
              addToCartBtn.disabled = false;
              addToCartText.textContent = 'ADD TO BAG';
              if (stockBadge) {
                stockBadge.textContent = 'IN STOCK';
                stockBadge.className = 'rb-badge rb-badge--gold';
              }
            } else {
              addToCartBtn.disabled = true;
              addToCartText.textContent = 'SOLD OUT';
              if (stockBadge) {
                stockBadge.textContent = 'SOLD OUT';
                stockBadge.className = 'rb-badge rb-badge--soldout';
              }
            }
          }

          // Update Gallery Media associated with this variant
          const mediaId = matchedOption.getAttribute('data-media-id');
          if (mediaId) {
            const localGallery = productSection.querySelector('[data-product-gallery]');
            if (localGallery && localGallery.roopbaniGallery) {
              localGallery.roopbaniGallery.selectMedia(mediaId);
            } else if (window.RoopbaniGallery) {
              window.RoopbaniGallery.selectMedia(mediaId);
            }
          }

          // Update URL
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.set('variant', matchedOption.value);
          window.history.replaceState({}, '', newUrl.toString());
        }
      };

      radios.forEach(radio => radio.addEventListener('change', updateSelectedVariant));
    }
  };

  /* --------------------------------------------------------------------------
     4b. Product Gallery Controller (Desktop Thumbnails + Mobile Swipe + Lightbox)
     -------------------------------------------------------------------------- */
  const ProductGalleryController = {
    init() {
      const galleries = document.querySelectorAll('[data-product-gallery]');
      galleries.forEach((gallery) => this.setupGallery(gallery));
    },

    setupGallery(gallery) {
      const viewport = gallery.querySelector('[data-gallery-viewport]');
      if (!viewport) return;

      const slides = gallery.querySelectorAll('.rb-product-gallery__slide');
      const thumbnails = gallery.querySelectorAll('[data-thumbnail-id]');
      const dots = gallery.querySelectorAll('[data-dot-index]');
      const prevBtn = gallery.querySelector('[data-gallery-prev]');
      const nextBtn = gallery.querySelector('[data-gallery-next]');
      const zoomBtn = gallery.querySelector('[data-lightbox-open]');

      let currentIndex = 0;

      const showSlide = (index) => {
        if (index < 0 || index >= slides.length) return;
        currentIndex = index;

        // Desktop main slide transition
        slides.forEach((slide, i) => {
          const isActive = (i === currentIndex);
          slide.classList.toggle('is-active', isActive);
          if (isActive) {
            slide.setAttribute('aria-hidden', 'false');
          } else {
            slide.setAttribute('aria-hidden', 'true');
            const video = slide.querySelector('video');
            if (video && !video.paused) video.pause();
          }
        });

        // Mobile scroll viewport into view
        const targetSlide = slides[currentIndex];
        if (targetSlide && window.innerWidth <= 991) {
          viewport.scrollTo({
            left: targetSlide.offsetLeft,
            behavior: 'smooth'
          });
        }

        // Thumbnails active state
        thumbnails.forEach((thumb, i) => {
          const isSelected = (i === currentIndex);
          thumb.classList.toggle('is-active', isSelected);
          thumb.setAttribute('aria-selected', isSelected ? 'true' : 'false');
          if (isSelected) {
            thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
          }
        });

        // Mobile dots active state
        dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === currentIndex);
        });
      };

      // Prev / Next arrow buttons
      if (prevBtn) {
        prevBtn.addEventListener('click', (e) => {
          e.preventDefault();
          showSlide((currentIndex - 1 + slides.length) % slides.length);
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', (e) => {
          e.preventDefault();
          showSlide((currentIndex + 1) % slides.length);
        });
      }

      // Thumbnail clicks
      thumbnails.forEach((thumb, index) => {
        thumb.addEventListener('click', (e) => {
          e.preventDefault();
          showSlide(index);
        });
      });

      // Mobile dots clicks
      dots.forEach((dot, index) => {
        dot.addEventListener('click', (e) => {
          e.preventDefault();
          showSlide(index);
        });
      });

      // Mobile scroll sync (scroll-snap event)
      let scrollTimer = null;
      viewport.addEventListener('scroll', () => {
        if (window.innerWidth > 991) return;
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => {
          const scrollPos = viewport.scrollLeft;
          const slideWidth = viewport.clientWidth;
          const newIndex = Math.round(scrollPos / slideWidth);
          if (newIndex !== currentIndex && newIndex >= 0 && newIndex < slides.length) {
            currentIndex = newIndex;
            dots.forEach((dot, i) => dot.classList.toggle('is-active', i === currentIndex));
            thumbnails.forEach((thumb, i) => thumb.classList.toggle('is-active', i === currentIndex));
          }
        }, 50);
      }, { passive: true });

      // Lightbox / Zoom modal handler
      if (zoomBtn) {
        const modalId = zoomBtn.getAttribute('data-lightbox-open');
        const modal = modalId ? document.getElementById(modalId) : document.querySelector('.rb-lightbox-modal');
        if (modal) {
          const targetImg = modal.querySelector('[data-lightbox-target-img]');
          zoomBtn.addEventListener('click', (e) => {
            e.preventDefault();
            const activeSlide = slides[currentIndex] || slides[0];
            const img = activeSlide ? activeSlide.querySelector('img') : null;
            if (img && targetImg) {
              targetImg.src = img.getAttribute('data-full-src') || img.src;
              targetImg.alt = img.alt || '';
            }
            modal.classList.add('is-active');
            modal.setAttribute('aria-hidden', 'false');
            document.body.style.overflow = 'hidden';
          });
        }
      }

      // Expose for VariantController
      const galleryApi = {
        selectMedia(mediaId) {
          if (!mediaId) return;
          const targetIndex = Array.from(slides).findIndex(
            (slide) => slide.getAttribute('data-media-id') === String(mediaId)
          );
          if (targetIndex !== -1) {
            showSlide(targetIndex);
          }
        },
        showSlide
      };
      gallery.roopbaniGallery = galleryApi;
      window.RoopbaniGallery = galleryApi;
    }
  };

  /* --------------------------------------------------------------------------
     4c. Universal Quantity Stepper Controller
     -------------------------------------------------------------------------- */
  const QuantityStepperController = {
    init() {
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('.rb-qty-stepper button[data-qty-change]');
        if (!btn) return;
        if (btn.closest('#RoopbaniCartDrawer')) return;

        const stepper = btn.closest('.rb-qty-stepper');
        const input = stepper ? stepper.querySelector('input[type="number"]') : null;
        if (!input) return;

        const delta = parseInt(btn.getAttribute('data-qty-change'), 10) || 0;
        const currentVal = parseInt(input.value, 10) || 1;
        const minVal = parseInt(input.getAttribute('min'), 10) || 1;
        const maxVal = parseInt(input.getAttribute('max'), 10) || 9999;
        const newVal = Math.max(minVal, Math.min(maxVal, currentVal + delta));
        input.value = newVal;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  };

  /* --------------------------------------------------------------------------
     5. Predictive Live Search Modal
     -------------------------------------------------------------------------- */
  const SearchController = {
    init() {
      this.modal = document.getElementById('RoopbaniSearchModal');
      if (!this.modal) return;

      this.input = this.modal.querySelector('#SearchInputHeader');
      this.resultsContainer = this.modal.querySelector('[data-search-live-results]');
      this.resultsGrid = this.modal.querySelector('[data-search-results-grid]');
      this.trendingBox = this.modal.querySelector('[data-search-trending]');
      this.debounceTimer = null;

      this.bindEvents();
    },

    bindEvents() {
      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-search-open]')) {
          e.preventDefault();
          this.open();
        } else if (e.target.closest('[data-search-close]')) {
          e.preventDefault();
          this.close();
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.modal.classList.contains('is-active')) {
          this.close();
        }
      });

      if (this.input) {
        this.input.addEventListener('input', () => {
          clearTimeout(this.debounceTimer);
          const query = this.input.value.trim();
          if (query.length < 2) {
            if (this.resultsContainer) this.resultsContainer.style.display = 'none';
            if (this.trendingBox) this.trendingBox.style.display = 'block';
            return;
          }
          this.debounceTimer = setTimeout(() => this.fetchSuggestions(query), 250);
        });
      }
    },

    open() {
      this.modal.classList.add('is-active');
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      setTimeout(() => this.input && this.input.focus(), 150);
    },

    close() {
      this.modal.classList.remove('is-active');
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    },

    async fetchSuggestions(query) {
      try {
        const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=4&resources[options][unavailable_products]=show`;
        const res = await fetch(url);
        const data = await res.json();
        const products = data.resources.results.products;

        if (products && products.length > 0) {
          if (this.trendingBox) this.trendingBox.style.display = 'none';
          if (this.resultsContainer) this.resultsContainer.style.display = 'block';
          this.renderResults(products);
        } else {
          if (this.resultsGrid) {
            this.resultsGrid.innerHTML = `<p class="rb-no-results" style="padding: 20px 0; color: #888;">NO ARCHIVE PIECES FOUND FOR "${query.toUpperCase()}"</p>`;
          }
        }
      } catch (err) {
        console.error('Predictive search error:', err);
      }
    },

    renderResults(products) {
      if (!this.resultsGrid) return;
      this.resultsGrid.innerHTML = `
        <div class="rb-product-grid rb-product-grid--4-col">
          ${products.map(p => `
            <div class="rb-product-card">
              <div class="rb-product-card__media-wrap rb-ratio-portrait">
                <a href="${p.url}" class="rb-product-card__link">
                  <img src="${p.image}" class="rb-product-card__img" alt="${p.title}" loading="lazy">
                </a>
              </div>
              <div class="rb-product-card__content">
                <h3 class="rb-product-card__title"><a href="${p.url}">${p.title}</a></h3>
                <div class="rb-product-card__price">
                  <span class="rb-price__regular">${p.price}</span>
                </div>
              </div>
            </div>
          `).join('')}
        </div>
      `;
    }
  };

  /* --------------------------------------------------------------------------
     6. Mobile Drawer Controller
     -------------------------------------------------------------------------- */
  const MobileDrawerController = {
    init() {
      const drawer = document.getElementById('RoopbaniMobileDrawer');
      if (!drawer) return;

      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-mobile-menu-open]')) {
          e.preventDefault();
          drawer.classList.add('is-active');
          document.body.style.overflow = 'hidden';
        } else if (e.target.closest('[data-mobile-menu-close]')) {
          e.preventDefault();
          drawer.classList.remove('is-active');
          document.body.style.overflow = '';
        }
      });
    }
  };

  /* --------------------------------------------------------------------------
     7. General Modal Controller (Size Guide Modal)
     -------------------------------------------------------------------------- */
  const ModalController = {
    init() {
      document.addEventListener('click', (e) => {
        const openBtn = e.target.closest('[data-modal-open]');
        if (openBtn) {
          const targetId = openBtn.getAttribute('data-modal-open');
          const modal = document.getElementById(targetId);
          if (modal) {
            modal.classList.add('is-active');
            modal.setAttribute('aria-hidden', 'false');
            document.body.style.overflow = 'hidden';
          }
        }

        const closeBtn = e.target.closest('[data-modal-close], [data-lightbox-close]');
        if (closeBtn) {
          const modal = closeBtn.closest('.rb-modal');
          if (modal) {
            modal.classList.remove('is-active');
            modal.setAttribute('aria-hidden', 'true');
            document.body.style.overflow = '';
          }
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          const openModals = document.querySelectorAll('.rb-modal.is-active');
          openModals.forEach((m) => {
            m.classList.remove('is-active');
            m.setAttribute('aria-hidden', 'true');
            document.body.style.overflow = '';
          });
        }
      });
    }
  };

  /* --------------------------------------------------------------------------
     8. Announcement Bar Rotator Controller
     -------------------------------------------------------------------------- */
  const AnnouncementController = {
    init() {
      const bar = document.querySelector('.rb-announcement-bar');
      if (!bar) return;
      const rotator = bar.querySelector('.rb-announcement-rotator');
      if (!rotator) return;
      const items = rotator.querySelectorAll('.rb-announcement-rotator__item');
      if (items.length <= 1) return;

      const delay = parseInt(bar.getAttribute('data-delay'), 10) || 4000;
      let currentIndex = 0;

      setInterval(() => {
        items[currentIndex].classList.remove('is-active');
        currentIndex = (currentIndex + 1) % items.length;
        items[currentIndex].classList.add('is-active');
      }, delay);
    }
  };

  /* --------------------------------------------------------------------------
     Helper: Currency Formatter
     -------------------------------------------------------------------------- */
  function formatMoney(cents) {
    if (typeof cents === 'string') cents = parseInt(cents, 10);
    const amount = (cents / 100).toFixed(2);
    const format = window.Roopbani?.moneyFormat || '₹{{amount}}';
    return format.replace('{{amount}}', amount).replace('{{amount_with_comma_separator}}', amount);
  }

  /* --------------------------------------------------------------------------
     DOM Ready Bootstrapper
     -------------------------------------------------------------------------- */
  document.addEventListener('DOMContentLoaded', () => {
    HeaderController.init();
    HeroSliderController.init();
    CartController.init();
    VariantController.init();
    ProductGalleryController.init();
    QuantityStepperController.init();
    SearchController.init();
    MobileDrawerController.init();
    ModalController.init();
    AnnouncementController.init();
  });

})();

